/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/properties-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-properties-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/properties-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: name, address_line_1, city, full_address, trig_submarket_name
 * - Filterable fields: id, parent_property_id, property_type_id, property_status_id, building_class_id, primary_use_id, secondary_use_id, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, submarket_id, review_status_id, reviewed_by_user_profile_id, created_by_ingestion_job_id, snapshot_of_property_id, parking_type_id, construction_purpose_id, property_rights_id, is_test_data, unit_kind_id, archived_by_user_profile_id, archive_batch_id
 * - Sortable fields: name, year_built, building_sf, verified_at, created_at, updated_at, snapshot_as_of
 * - Required fields: id, type_specific_fields
 * - Readonly fields: id, county, source_summary, verified_at, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, created_at, updated_at, submarket_id, trig_submarket_name, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/properties-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-properties-api-client.ts (API client)
 * - client/api/generated_schemas/properties.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/properties-entity-config.ts
 * Public fields: 68/68 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PropertiesClient, PropertiesQueryOptions } from '../generated_clients/generated-properties-api-client.js';
import {
  PropertiesInsert,
  PropertiesUpdate,
  PropertiesFieldConfig
} from '../../types/generated_schemas/generated-properties-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/properties_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const propertiesKeys = {
  all: ['properties'] as const,
  lists: () => [...propertiesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<PropertiesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...propertiesKeys.lists(), { filters, ...page }] as const,
  details: () => [...propertiesKeys.all, 'detail'] as const,
  detail: (id: string) => [...propertiesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...propertiesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...propertiesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...propertiesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...propertiesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const usePropertiesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new PropertiesClient();
};

/**
 * Hook to get all properties with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with properties data
 */
export const usePropertiesList = (options?: PropertiesQueryOptions) => {
  const client = usePropertiesClient();
  
  return useQuery({
    queryKey: propertiesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single propertie by ID
 *
 * @param id - The propertie ID
 * @returns React Query result with single propertie data
 */
export const useProperties = (id: string) => {
  const client = usePropertiesClient();
  
  return useQuery({
    queryKey: propertiesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/properties
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/properties_business_hooks/
 */

/**
 * Hook to create a new propertie
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, type_specific_fields
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, county, source_summary, verified_at, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, created_at, updated_at, submarket_id, trig_submarket_name, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * @returns React Query mutation for creating propertie
 */
export const useCreateProperties = () => {
  const client = usePropertiesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PropertiesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertiesKeys.all });
    },
  });
};

/**
 * Hook to update an existing propertie
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, county, source_summary, verified_at, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, created_at, updated_at, submarket_id, trig_submarket_name, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * @returns React Query mutation for updating propertie
 */
export const useUpdateProperties = () => {
  const client = usePropertiesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PropertiesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: propertiesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: propertiesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a propertie
 *
 * @returns React Query mutation for deleting propertie
 */
export const useDeleteProperties = () => {
  const client = usePropertiesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertiesKeys.all });
    },
  });
};

/**
 * Hook to count properties with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, parent_property_id, property_type_id, property_status_id, building_class_id, primary_use_id, secondary_use_id, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, submarket_id, review_status_id, reviewed_by_user_profile_id, created_by_ingestion_job_id, snapshot_of_property_id, parking_type_id, construction_purpose_id, property_rights_id, is_test_data, unit_kind_id, archived_by_user_profile_id, archive_batch_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const usePropertiesCount = (filter?: Partial<Record<typeof PropertiesFieldConfig.filterable[number], any>>) => {
  const client = usePropertiesClient();
  
  return useQuery({
    queryKey: propertiesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const usePropertiesFieldConfig = () => {
  return PropertiesFieldConfig;
};